// Navbar.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Navbar.css";
import logo from "../assets/dev_logo.png";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";

const DESKTOP_NAVBAR_HEIGHT = 80;

const navItems = [
  { label: "Home", id: "hero" },
  { label: "About", id: "about" },
  { label: "Projects", id: "projects" },
  { label: "Contact", id: "contact" },
];

const socialLinks = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61578837325046",
    icon: <FaFacebookF />,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/thewedev",
    icon: <FaInstagram />,
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/the-we-dev-inc/",
    icon: <FaLinkedinIn />,
  },
];

const Navbar = ({ animate }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isDmOpen, setIsDmOpen] = useState(false);

  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  const rafRef = useRef(null);
  const servicesWrapRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const isOnHomePage = location.pathname === "/";
  const isServicesRoute = location.pathname.startsWith("/services");
  const isManpowerRoute = location.pathname === "/manpower";

  const getNavbarHeight = useCallback(() => {
    return window.innerWidth < 768 ? 70 : DESKTOP_NAVBAR_HEIGHT;
  }, []);

  const detectActiveSection = useCallback(() => {
    const scrollY = window.scrollY;
    setScrolled(scrollY > 50);

    if (!isOnHomePage) return;

    if (scrollY < 80) {
      setActiveSection("hero");
      return;
    }

    const offsetLine = getNavbarHeight() + 20;
    let current = "hero";

    navItems.forEach((item) => {
      const el = document.getElementById(item.id);
      if (!el) return;

      const rect = el.getBoundingClientRect();
      if (rect.top <= offsetLine && rect.bottom >= offsetLine) {
        current = item.id;
      }
    });

    setActiveSection(current);
  }, [getNavbarHeight, isOnHomePage]);

  useEffect(() => {
    const onScroll = () => {
      if (!isOnHomePage) return;
      if (rafRef.current) return;

      rafRef.current = requestAnimationFrame(() => {
        detectActiveSection();
        rafRef.current = null;
      });
    };

    window.addEventListener("scroll", onScroll);
    detectActiveSection();

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isOnHomePage, detectActiveSection]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
        setIsServicesOpen(false);
        setIsDmOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsServicesOpen(false);
    setIsDmOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Outside click closes dropdown (desktop only)
  useEffect(() => {
    const onDown = (e) => {
      if (window.innerWidth < 768) return;

      if (servicesWrapRef.current && !servicesWrapRef.current.contains(e.target)) {
        setIsServicesOpen(false);
        setIsDmOpen(false);
      }
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsServicesOpen(false);
        setIsDmOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsDmOpen(false);

    if (!isOnHomePage) {
      navigate("/");
      setTimeout(() => {
        const target = id === "hero" ? null : document.getElementById(id);
        if (!target) {
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
        const navH = getNavbarHeight();
        const y = target.getBoundingClientRect().top + window.pageYOffset - (navH - 10);
        window.scrollTo({ top: y, behavior: "smooth" });
      }, 50);
      return;
    }

    setActiveSection(id);

    if (id === "hero") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const target = document.getElementById(id);
    if (!target) return;

    const navH = getNavbarHeight();
    const y = target.getBoundingClientRect().top + window.pageYOffset - (navH - 10);
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const closeAllMenus = () => {
    setIsServicesOpen(false);
    setIsDmOpen(false);
    setIsMobileMenuOpen(false);
  };

  const goTo = (to) => {
    closeAllMenus();
    navigate(to);
  };

  const onServicesToggle = () => {
    setIsServicesOpen((p) => {
      const next = !p;
      if (!next) setIsDmOpen(false);
      return next;
    });
  };

  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
      {/* LEFT */}
      <div className={`navbar-left ${animate ? "active" : ""}`}>
        <Link to="/" onClick={() => window.scrollTo({ top: 0 })}>
          <img src={logo} alt="Logo" className="logo-image" />
        </Link>
      </div>

      {/* CENTER */}
      <ul className={`nav-links ${isMobileMenuOpen ? "mobile-menu-open" : ""}`}>
        {navItems.map((item) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              onClick={(e) => handleNavClick(e, item.id)}
              className={`nav-action ${
                isOnHomePage && activeSection === item.id ? "active" : ""
              }`}
            >
              {item.label}
            </a>
          </li>
        ))}

        {/* SERVICES */}
        <li ref={servicesWrapRef} className="nav-dropdown">
          <button
            className={`nav-action ${isServicesRoute ? "active" : ""}`}
            onClick={onServicesToggle}
            type="button"
          >
            Services ▾
          </button>

          {/* ✅ Desktop dropdown */}
          {isServicesOpen && (
            <div className="dropdown-menu show">
              {/* 1) Web Development */}
              <button
                type="button"
                className="dropdown-item dropdown-linklike"
                onClick={() => goTo("/services/web-development")}
              >
                Web Development
              </button>

              {/* 2) Digital Marketing -> submenu */}
              <div className={`dropdown-item has-sub ${isDmOpen ? "open" : ""}`}>
                <button
                  type="button"
                  className="sub-trigger"
                  onClick={() => setIsDmOpen((p) => !p)}
                >
                  Digital Marketing <span className="sub-caret">▸</span>
                </button>

                {isDmOpen && (
                  <div className="submenu">
                    <button
                      type="button"
                      className="submenu-item"
                      onClick={() => goTo("/services/social-media-management")}
                      title="Social Media Management"
                    >
                      Social Media Management
                    </button>
                    <button
                      type="button"
                      className="submenu-item"
                      onClick={() => goTo("/services/search-engine-optimization")}
                      title="Search Engine Optimization"
                    >
                      Search Engine Optimization
                    </button>
                  </div>
                )}
              </div>

              {/* 3) App Development */}
              <button
                type="button"
                className="dropdown-item dropdown-linklike"
                onClick={() => goTo("/services/app-development")}
              >
                App Development
              </button>
            </div>
          )}

          {/* ✅ Mobile dropdown */}
          <div className={`dropdown-menu-mobile ${isServicesOpen ? "show" : ""}`}>
            <Link
              to="/services/web-development"
              className="dropdown-item-mobile"
              onClick={closeAllMenus}
            >
              Web Development
            </Link>

            <button
              type="button"
              className={`dropdown-item-mobile dm-mobile ${isDmOpen ? "open" : ""}`}
              onClick={() => setIsDmOpen((p) => !p)}
            >
              Digital Marketing{" "}
              <span className="sub-caret-mobile">{isDmOpen ? "▾" : "▸"}</span>
            </button>

            {isDmOpen && (
              <div className="mobile-submenu">
                <Link
                  to="/services/social-media-management"
                  className="dropdown-item-mobile sub"
                  onClick={closeAllMenus}
                  title="Social Media Management"
                >
                  Social Media Management
                </Link>
                <Link
                  to="/services/search-engine-optimization"
                  className="dropdown-item-mobile sub"
                  onClick={closeAllMenus}
                  title="Search Engine Optimization"
                >
                  Search Engine Optimization
                </Link>
              </div>
            )}

            <Link
              to="/services/app-development"
              className="dropdown-item-mobile"
              onClick={closeAllMenus}
            >
              App Development
            </Link>
          </div>
        </li>

        {/* MAN POWER */}
        <li>
          <Link
            to="/manpower"
            className={`nav-action ${isManpowerRoute ? "active" : ""}`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            ManPower
          </Link>
        </li>

        {/* ✅ Mobile socials ONLY when menu open */}
        {isMobileMenuOpen && (
          <li className="mobile-social-wrapper">
            <div className="mobile-social">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </li>
        )}
      </ul>

      {/* RIGHT */}
      <div className="navbar-right">
        {/* ✅ Desktop socials */}
        <div className="social-links desktop-social">
          {socialLinks.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noreferrer"
              aria-label={s.label}
            >
              {s.icon}
            </a>
          ))}
        </div>

        <button
          className={`mobile-menu-btn ${isMobileMenuOpen ? "active" : ""}`}
          onClick={() => {
            setIsMobileMenuOpen((p) => !p);
            setIsServicesOpen(false);
            setIsDmOpen(false);
          }}
          type="button"
          aria-label="Toggle Menu"
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
